import {
    PiArchiveBold,
    PiCubeBold,
    PiFolderOpenBold,
    PiStackBold,
    PiTagBold,
    PiTruckBold,
} from 'react-icons/pi';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { ProductCatalogFilter, ProductCatalogStats, ProductFilterOptions } from './types';

const TYPE_LABELS: Record<string, string> = {
    equipamento: 'Equipamento',
    peca: 'Peça',
    acessorio: 'Acessório',
    kit: 'Kit',
    servico: 'Serviço',
    outro: 'Outro',
};

type Props = {
    active: ProductCatalogFilter;
    onChange: (filter: ProductCatalogFilter) => void;
    options?: ProductFilterOptions;
};

function filterKey(filter: ProductCatalogFilter): string {
    if (filter.type === 'all') return 'all';
    if (filter.type === 'category' || filter.type === 'group' || filter.type === 'type' || filter.type === 'series') {
        return `${filter.type}:${filter.value}`;
    }
    return filter.type;
}

function countFor(filter: ProductCatalogFilter, stats?: ProductCatalogStats): number | undefined {
    if (!stats) return undefined;
    switch (filter.type) {
        case 'all':         return stats.total;
        case 'in_stock':    return stats.in_stock;
        case 'out_of_stock':return stats.out_of_stock;
        case 'in_transit':  return stats.in_transit;
        case 'category':    return stats.by_category[filter.value];
        default:            return undefined;
    }
}

export default function ProductCatalogSidebar({ active, onChange, options }: Props) {
    const { t } = useI18n();
    const stats = options?.stats;
    const activeKey = filterKey(active);

    const quickFilters: { filter: ProductCatalogFilter; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
        { filter: { type: 'type', value: 'equipamento' }, label: t('v2.products.filter_equipment'), icon: PiCubeBold },
        { filter: { type: 'all' },          label: t('v2.products.filter_all'), icon: PiFolderOpenBold },
        { filter: { type: 'in_stock' },     label: t('v2.products.filter_in_stock'), icon: PiStackBold },
        { filter: { type: 'in_transit' },   label: t('v2.products.filter_in_transit'), icon: PiTruckBold },
        { filter: { type: 'out_of_stock' }, label: t('v2.products.filter_out_of_stock'), icon: PiArchiveBold },
    ];

    return (
        <aside className="flex h-full flex-col border-r border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700/60 dark:bg-gray-800/40">
            <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Estoque
            </p>
            <nav className="space-y-0.5">
                {quickFilters.map(({ filter, label, icon }) => (
                    <SidebarButton
                        key={filterKey(filter)}
                        label={label}
                        icon={icon}
                        count={countFor(filter, stats)}
                        active={activeKey === filterKey(filter)}
                        onClick={() => onChange(filter)}
                    />
                ))}
            </nav>

            {(options?.categories.length ?? 0) > 0 && (
                <>
                    <p className="mb-3 mt-6 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Categorias
                    </p>
                    <nav className="max-h-48 space-y-0.5 overflow-y-auto">
                        {options!.categories.map((category) => {
                            const filter: ProductCatalogFilter = { type: 'category', value: category };
                            return (
                                <SidebarButton
                                    key={category}
                                    label={category}
                                    icon={PiFolderOpenBold}
                                    count={stats?.by_category[category]}
                                    active={activeKey === filterKey(filter)}
                                    onClick={() => onChange(filter)}
                                />
                            );
                        })}
                    </nav>
                </>
            )}

            {(options?.groups.length ?? 0) > 0 && (
                <>
                    <p className="mb-3 mt-6 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Grupos
                    </p>
                    <nav className="max-h-36 space-y-0.5 overflow-y-auto">
                        {options!.groups.slice(0, 12).map((group) => {
                            const filter: ProductCatalogFilter = { type: 'group', value: group };
                            return (
                                <SidebarButton
                                    key={group}
                                    label={group}
                                    icon={PiTagBold}
                                    active={activeKey === filterKey(filter)}
                                    onClick={() => onChange(filter)}
                                />
                            );
                        })}
                    </nav>
                </>
            )}

            {(options?.types.length ?? 0) > 0 && (
                <>
                    <p className="mb-3 mt-6 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Tipos
                    </p>
                    <nav className="max-h-36 space-y-0.5 overflow-y-auto">
                        {options!.types.map((type) => {
                            const filter: ProductCatalogFilter = { type: 'type', value: type };
                            return (
                                <SidebarButton
                                    key={type}
                                    label={TYPE_LABELS[type] ?? type}
                                    icon={PiTagBold}
                                    active={activeKey === filterKey(filter)}
                                    onClick={() => onChange(filter)}
                                />
                            );
                        })}
                    </nav>
                </>
            )}
        </aside>
    );
}

function SidebarButton({
    label,
    icon: Icon,
    count,
    active,
    onClick,
}: {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    count?: number;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition',
                active
                    ? 'bg-white font-semibold text-gray-900 shadow-sm ring-1 ring-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600'
                    : 'text-gray-600 hover:bg-white/70 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-gray-200',
            )}
        >
            <Icon className={cn('size-4 shrink-0', active ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500')} />
            <span className="min-w-0 flex-1 truncate">{label}</span>
            {count !== undefined && (
                <span className={cn('shrink-0 text-xs tabular-nums', active ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500')}>
                    {count}
                </span>
            )}
        </button>
    );
}
